import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LogOut, Moon, Sun, User as UserIcon } from "lucide-react";

import { cn } from "lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { logoutUser } from "slices/auth/login/thunk";
import { changeLayoutMode } from "slices/thunk";
import { applyTheme, getStoredTheme, storeTheme, ThemeMode } from "helpers/theme";
import { getAvatarUrl } from "helpers/avatar";

const SidebarFooter = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.Login);

  const [name, setName] = useState("User system");
  const [mode, setMode] = useState<ThemeMode>(getStoredTheme());
  // The signed-in user is considered online while the app is open.
  const online = true;

  useEffect(() => {
    setName(localStorage.getItem("name") || "User system");
  }, []);

  const displayName = name
    ? name.charAt(0).toUpperCase() + name.slice(1)
    : "User system";

  const initials = (name || "U")
    .split(" ")
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const avatarSrc = user?.avatar || getAvatarUrl(user?.email || name);

  const toggleTheme = () => {
    const next: ThemeMode = mode === "dark" ? "light" : "dark";
    setMode(next);
    storeTheme(next);
    applyTheme(next);
    dispatch(changeLayoutMode(next));
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  return (
    <div className="flex items-center gap-2 border-t border-border/60 px-3 py-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1.5 text-left text-sm font-medium",
              "transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            <span className="relative shrink-0">
              <Avatar className="h-9 w-9">
                <AvatarImage src={avatarSrc} alt={displayName} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              {/* Online status indicator on the avatar. */}
              <span
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
                  online ? "bg-success" : "bg-destructive"
                )}
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-foreground">{displayName}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {online ? "Active now" : "Offline"}
              </span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-60">
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="relative shrink-0">
              <Avatar className="h-10 w-10">
                <AvatarImage src={avatarSrc} alt={displayName} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <span
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-popover",
                  online ? "bg-success" : "bg-destructive"
                )}
              />
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-foreground">
                {displayName}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    online ? "bg-success" : "bg-destructive"
                  )}
                />
                {online ? "Active now" : "Offline"}
              </div>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/setting")}>
            <UserIcon />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            className="text-destructive focus:text-destructive"
          >
            <LogOut />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dark mode toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label="Toggle dark mode"
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-lg",
          "text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        )}
      >
        {mode === "dark" ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Moon className="h-4 w-4" />
        )}
      </button>
    </div>
  );
};

export default SidebarFooter;
